<template>
  <listing-layout>
    <div slot="heading" class="mb-3">
      <h1 class="h2 mb-0">Export Reports</h1>
      <div role="alert" class="mt-3 alert alert-warning col-12" v-if="error">{{ error }}</div>
    </div>
    <div slot="content" class="form mb-sm-3 d-flex justify-content-between bg-lighter">
      <div class="col-12 px-0 mt-2">
        <form novalidate class="needs-validation mb-3 pt-3 p-1 p-sm-3" @submit.prevent="runReport">
          <div class="row">
            <div class="col-12 col-md-6 col-xl-4 form-group">
              <label for="report-type">Please select a report to begin</label>
              <select class="form-control" id="report-type" required v-model="report">
                <option selected disabled value="">-- Select Report Type --</option>
                <option value="mailinglist" v-if="isAdmin">Mailing List</option>
                <option value="activemembers" v-if="isAdmin">Registered Users</option>
                <option value="results" v-if="isAdmin">Results</option>
                <option value="secretary-financial-members">Financial Members</option>
                <option value="calendar-of-events">Calendar of Events</option>
                <option value="profiles" v-if="isAdmin">All Profiles</option>
                <option value="standings-points" v-if="isAdmin">Standings Points</option>
                <option value="app-import" v-if="isAdmin">App Import</option>
                <option value="horse-progress-report" v-if="isAdmin">Horse Progress Report</option>
              </select>
            </div>
            <div v-if="report === 'horse-progress-report'" class="col-12 col-md-6 col-xl-4 form-group">
              <label for="report-horse">Horse</label>
              <app-multiselect
                id="report-horse"
                v-model="horse"
                :options="horseOptions"
                :loading="horseSearchLoading"
                :internal-search="false"
                placeholder="Search horses..."
                label="name"
                track-by="id"
                @search-change="onHorseSearch"
              />
            </div>
            <div v-if="report === 'standings-points'" class="col-12 col-md-6 col-xl-4">
              <season-select v-model="year" />
            </div>
            <div v-if="report === 'standings-points'" class="col-12 col-md-6 col-xl-4">
              <zone-select v-model="zone" @loaded="zones = $event" />
            </div>
            <div v-if="report === 'results' || report === 'calendar-of-events'" class="col-12 col-md-6 col-xl-8">
              <div class="row">
                <div class="col-12 col-xl-6">
                  <div class="form-group">
                    <label for="startDate">Date&nbsp;<small>(from)</small></label>
                    <input
                    class="form-control form-date w-100"
                    id="startDate" type="date"
                    pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}"
                    v-model="calendarFilter.from_date"
                    :max="calendarFilter.to_date"
                    @change="wasValidated = true"
                    required>
                    <div class="invalid-tooltip">
                      <span v-if="calendarFilter.from_date > calendarFilter.to_date">Ensure the "from" date is before the "to" date.</span>
                      <span v-else>Please include the Date{{ isLegacy() ? ' in the format yyyy-mm-dd' : ''}}.</span>
                    </div>
                  </div>
                </div>
                <div class="col-12 col-xl-6">
                  <div class="form-group">
                    <label for="endDate">Date&nbsp;<small>(to)</small></label>
                    <input
                    class="form-control form-date w-100"
                    id="endDate" type="date"
                    pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}"
                    v-model="calendarFilter.to_date"
                    :min="calendarFilter.from_date"
                    @change="wasValidated = true"
                    required>
                    <div class="invalid-tooltip">
                      <span v-if="calendarFilter.from_date > calendarFilter.to_date">Ensure the "from" date is before the "to" date.</span>
                      <span v-else>Please include the Date{{ isLegacy() ? ' in the format yyyy-mm-dd' : ''}}.</span>
                    </div>
                  </div>
                </div>
                <!--<div class="col-12 col-xl-6" v-if="report === 'calendar-of-events'">
                  <div class="form-group">
                    <label for="zone">Zone</label>
                    <select class="form-control" id="zone" v-model="selectedZone">
                      <option disabled value="">Please Select Zone (if applicable)</option>
                      <option v-for="(zone, zoneIndex) in listZones" :key="zoneIndex" :value="zone.id">{{zone.code}}</option>
                    </select>
                    <div class="invalid-tooltip">
                      <span>Please include the Zone.</span>
                    </div>
                  </div>
                </div>-->
              </div>
            </div>
            <div v-else-if="report === 'profiles' || report === 'app-import' || report === 'horse-progress-report'" class="col-12 col-md-6 col-xl-8">
            </div>
            <div v-else-if="report && report !== 'standings-points'" class="col-12 col-md-6 col-xl-8">
              <div class="row">
                <div class="col-12 col-md-6 form-group">
                  <label for="year">Reporting Year</label>
                  <select id="year" class="form-control pr-5" v-model="year">
                    <option v-for="(year, yearIndex) in years" :key="yearIndex" :value="year.id">{{year.name}}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <button type="submit" class="btn btn-primary btn-block-mob mb-3 mb-lg-0 ml-lg-auto" :disabled="!canSubmit">
                <div v-if="processing" class="d-flex align-items-center">
                  <div class="spinner-border spinner-border-sm text-light mr-2" role="status"></div>Downloading
                </div>
                <span v-else-if="report">Download Report</span>
                <span v-else>Select a report to proceed</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </listing-layout>
</template>

<script>
import axios from 'axios'
import _ from 'lodash'
import listingLayout from '@/components/ListingLayout'
import LEGACYBROWSERS from '@/mixins/legacyBrowsers.js'
import SeasonSelect from '@/components/SeasonSelect'
import ZoneSelect from '@/components/ZoneSelect'
import AppMultiselect from '@/components/AppMultiselect'
import horsesApi from '@/services/api/horses'

export default {
  name: 'Report',
  mixins: [LEGACYBROWSERS],
  components: {
    'listing-layout': listingLayout,
    SeasonSelect,
    ZoneSelect,
    AppMultiselect
  },
  data() {
    return {
      horse: null,
      horseOptions: [],
      horseSearchLoading: false,
      calendarFilter: {
        to_date: this.getHolderDate(),
        from_date: this.getHolderDate()
      },
      selectedZone: '',
      zone: 0,
      zones: [],
      zoneName: '',
      fileName: '',
      processing: false,
      error: '',
      report: '',
      year: '',
      years: [],
      userRole: localStorage.role
    }
  },
  created() {
    this.debouncedSearchHorses = _.debounce(this.searchHorses, 500)
  },
  mounted() {
    this.getYears()
  },
  watch: {
    zone(id) {
      const found = this.zones.find(z => z.id === id)
      this.zoneName = found ? found.code : ''
    },
    report() {
      this.error = ''
      this.horse = null
      this.horseOptions = []
      switch (this.report) {
        case 'results':
          break
        case 'activemembers':
          this.fileName = 'registered-users-list'
          this.year = this.getYear()
          break
        case 'mailinglist':
          this.fileName = 'mail-list'
          this.year = this.getYear()
          break
        case 'secretary-financial-members':
          this.fileName = 'financial-members'
          this.year = this.getYear()
          break
        case 'calendar-of-events':
          this.fileName = 'calendar-of-events'
          this.year = ''
          break
        default:
          if (this.report) {
            this.fileName = this.report
          }
          break
      }
    }
  },
  computed: {
    listZones() {
      return this.$store.state.listZones
    },
    isAdmin() {
      return this.userRole === 'Admin' || this.userRole === 'SuperAdmin'
    },
    canSubmit() {
      if (!this.report) return false
      if (this.report === 'horse-progress-report') return !!this.horse
      return true
    }
  },
  methods: {
    onHorseSearch(query) {
      this.horseSearchLoading = true
      this.debouncedSearchHorses(query)
    },
    searchHorses(query) {
      horsesApi.search(query).then((response) => {
        this.horseOptions = response.data.data
      }).catch(() => {
        this.horseOptions = []
      }).finally(() => {
        this.horseSearchLoading = false
      })
    },
    getYear() {
      // Get year data
      const today = this.$store.state.currentDate ? new Date(this.$store.state.currentDate) : new Date()
      return today.getFullYear()
    },
    getYears() {
      for (let Y = this.getYear(); Y >= 2019; Y--) {
        this.years.push({ id: Y, name: `${Y}` })
      }
    },
    getHolderDate() {
      const d = this.$store.state.currentDate ? new Date(this.$store.state.currentDate) : new Date()
      // 0 indexed for Jan so add 1 to get current
      const day = d.getDate()
      const dayString = (`0${day}`).slice(-2)

      const month = d.getMonth() + 1
      const monthString = (`0${month}`).slice(-2)

      const year = d.getFullYear().toString()

      // Return the start of the ACA year
      return [year, monthString, dayString].join('-')
    },
    runReport() {
      event.preventDefault()

      switch (this.report) {
        case 'results':
          this.exportResults()
          break
        case 'profiles':
          this.exportProfiles()
          break
        case 'standings-points':
          this.exportStandingsPoints()
          break
        case 'app-import':
          this.exportAppImport()
          break
        case 'horse-progress-report':
          this.exportHorseProgressReport()
          break
        case 'activemembers':
        case 'mailinglist':
        case 'secretary-financial-members':
        case 'calendar-of-events':
        default:
          this.exportList()
          break
      }
    },
    exportResults() {
      this.processing = true
      axios.get(`${this.$api}/export/results?from_date=${this.calendarFilter.from_date}&to_date=${this.calendarFilter.to_date}`).then((response) => {
        this.processing = false

        if (window.navigator.msSaveOrOpenBlob) {
          const blobObject = new Blob([response.data])
          window.navigator.msSaveOrOpenBlob(blobObject, `results.csv`)
        } else if (this.isIOS()) {
          const reader = new FileReader()
          reader.fileName = `results.csv`
          const out = new Blob([response.data], { type: 'data:text/csv;charset=utf-8' })

          reader.onload = () => {
            window.location.href = reader.result
          }
          reader.readAsDataURL(out)
        } else {
          const url = window.URL ? window.URL.createObjectURL(new Blob([response.data], { type: 'data:text/csv;charset=utf-8' })) : window.webkitURL.createObjectURL(new Blob([response.data], { type: 'data:text/csv;charset=utf-8' }))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `results.csv`)
          document.body.appendChild(link)
          link.click()
          setTimeout(() => {
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
          }, 0)
        }
      }).catch((error) => {
        this.processing = false
        // eslint-disable-next-line
        console.log(error)
        this.$rollbar.error(error)
      })
    },
    exportList() {
      this.processing = true
      let params = ''
      if (this.report === 'calendar-of-events') {
        params += `?from_date=${this.calendarFilter.from_date}&to_date=${this.calendarFilter.to_date}`
        // For when they ask for zone filtering for calendar of events
        // params += this.selectedZone ? `&zone=${this.selectedZone}` : ''
      }
      axios.post(`${this.$api}/admin/export/${this.report}/${this.year}${params}`).then((response) => {
        this.processing = false

        if (window.navigator.msSaveOrOpenBlob) {
          const blobObject = new Blob([response.data])
          window.navigator.msSaveOrOpenBlob(blobObject, `${this.fileName}-${this.year}.csv`)
        } else if (this.isIOS()) {
          const reader = new FileReader()
          reader.fileName = `${this.fileName}.csv`
          const out = new Blob([response.data], { type: 'data:text/csv;charset=utf-8' })

          reader.onload = () => {
            window.location.href = reader.result
          }
          reader.readAsDataURL(out)
        } else {
          const url = window.URL ? window.URL.createObjectURL(new Blob([response.data], { type: 'data:text/csv;charset=utf-8' })) : window.webkitURL.createObjectURL(new Blob([response.data], { type: 'data:text/csv;charset=utf-8' }))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `${this.fileName}-${this.year}.csv`)
          document.body.appendChild(link)
          link.click()
          setTimeout(() => {
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
          }, 0)
        }
      }).catch((error) => {
        this.processing = false
        // eslint-disable-next-line
        console.log(error)
      })
    },
    exportStandingsPoints() {
      this.processing = true
      const url = this.zone
        ? `${this.$api}/admin/export/standings-points/${this.year}/${this.zone}`
        : `${this.$api}/admin/export/standings-points/${this.year}`

      axios.get(url, { responseType: 'blob' }).then((response) => {
        this.processing = false
        const zonePart = this.zoneName ? `${this.zoneName.slice(0, -1)} ZONE ` : 'NATIONAL ZONE '
        const fileName = `${zonePart}${this.year} ${this.year + 1}.xlsx`
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })

        if (window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(blob, fileName)
        } else if (this.isIOS()) {
          const reader = new FileReader()
          reader.onload = () => { window.location.href = reader.result }
          reader.readAsDataURL(blob)
        } else {
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.setAttribute('download', fileName)
          document.body.appendChild(link)
          link.click()
          setTimeout(() => {
            document.body.removeChild(link)
            window.URL.revokeObjectURL(link.href)
          }, 0)
        }
      }).catch((error) => {
        this.processing = false
        console.log(error)
        this.$rollbar.error(error)
      })
    },
    exportAppImport() {
      this.processing = true
      this.error = ''
      axios.get(`${this.$api}/admin/export/app-import`).then((response) => {
        this.processing = false

        if (response.data && response.data.status === 'undefined_error') {
          this.error = 'A server error occurred. Please try again later.'
          return
        }

        if (window.navigator.msSaveOrOpenBlob) {
          const blobObject = new Blob([response.data])
          window.navigator.msSaveOrOpenBlob(blobObject, `app-import.csv`)
        } else if (this.isIOS()) {
          const reader = new FileReader()
          reader.fileName = `app-import.csv`
          const out = new Blob([response.data], { type: 'data:text/csv;charset=utf-8' })

          reader.onload = () => {
            window.location.href = reader.result
          }
          reader.readAsDataURL(out)
        } else {
          const url = window.URL ? window.URL.createObjectURL(new Blob([response.data], { type: 'data:text/csv;charset=utf-8' })) : window.webkitURL.createObjectURL(new Blob([response.data], { type: 'data:text/csv;charset=utf-8' }))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `app-import.csv`)
          document.body.appendChild(link)
          link.click()
          setTimeout(() => {
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
          }, 0)
        }
      }).catch((error) => {
        this.processing = false
        console.log(error)
        this.$rollbar.error(error)
      })
    },
    exportProfiles() {
      this.processing = true
      axios.get(`${this.$api}/admin/export/profiles`).then((response) => {
        this.processing = false

        if (window.navigator.msSaveOrOpenBlob) {
          const blobObject = new Blob([response.data])
          window.navigator.msSaveOrOpenBlob(blobObject, `profiles-report.csv`)
        } else if (this.isIOS()) {
          const reader = new FileReader()
          reader.fileName = `profiles-report.csv`
          const out = new Blob([response.data], { type: 'data:text/csv;charset=utf-8' })

          reader.onload = () => {
            window.location.href = reader.result
          }
          reader.readAsDataURL(out)
        } else {
          const url = window.URL ? window.URL.createObjectURL(new Blob([response.data], { type: 'data:text/csv;charset=utf-8' })) : window.webkitURL.createObjectURL(new Blob([response.data], { type: 'data:text/csv;charset=utf-8' }))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `profiles-report.csv`)
          document.body.appendChild(link)
          link.click()
          setTimeout(() => {
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
          }, 0)
        }
      }).catch((error) => {
        this.processing = false
        console.log(error)
        this.$rollbar.error(error)
      })
    },
    exportHorseProgressReport() {
      if (!this.horse) return

      this.processing = true
      const horseName = this.horse.name.replace(/[^A-Za-z0-9]+/g, '-')
      const fileName = `horse-progress-report-${horseName}.csv`

      axios.get(`${this.$api}/admin/export/horse-progress-report?horse_id=${this.horse.id}`).then((response) => {
        this.processing = false

        if (window.navigator.msSaveOrOpenBlob) {
          const blobObject = new Blob([response.data])
          window.navigator.msSaveOrOpenBlob(blobObject, fileName)
        } else if (this.isIOS()) {
          const reader = new FileReader()
          reader.fileName = fileName
          const out = new Blob([response.data], { type: 'data:text/csv;charset=utf-8' })

          reader.onload = () => {
            window.location.href = reader.result
          }
          reader.readAsDataURL(out)
        } else {
          const url = window.URL ? window.URL.createObjectURL(new Blob([response.data], { type: 'data:text/csv;charset=utf-8' })) : window.webkitURL.createObjectURL(new Blob([response.data], { type: 'data:text/csv;charset=utf-8' }))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', fileName)
          document.body.appendChild(link)
          link.click()
          setTimeout(() => {
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
          }, 0)
        }
      }).catch((error) => {
        this.processing = false
        console.log(error)
        this.$rollbar.error(error)
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/views/Export/Report.vue